import { useCallback, useEffect, useRef, useState } from "react";
import type { DataBlockEventData, ToolStep } from "./useAgentChat";

export interface ConversationProgress {
  active: boolean;
  turn_id: string | null;
  started_at: number | null;
  assistant_message_id: string | null;
  user_message_id: string | null;
  events: { event: string; data: Record<string, unknown> }[];
}

/** Rebuild one turn from a checkpoint; never append it to a previous poll. */
export function replayProgress(snapshot: ConversationProgress) {
  return replayProgressEvents(snapshot.events);
}

export function findRunningToolStep(
  steps: ToolStep[],
  data: { tool: unknown; call_id?: unknown },
): number {
  if (typeof data.call_id === "string" && data.call_id) {
    return steps.findIndex(
      (step) => step.status === "running" && step.callId === data.call_id,
    );
  }
  const named = steps.findIndex(
    (step) => step.status === "running" && step.tool === data.tool,
  );
  return named >= 0
    ? named
    : steps.findIndex((step) => step.status === "running");
}

export function replayProgressEvents(events: ConversationProgress["events"]) {
  let content = "";
  let phase: "fetching_context" | "generating" | "tool_executing" =
    "fetching_context";
  const steps: ToolStep[] = [];
  const blocks = new Map<string, DataBlockEventData>();
  for (const { event, data } of events) {
    if (event === "token") {
      content += String(data.content ?? "");
      phase = "generating";
    } else if (
      event === "status" &&
      ["fetching_context", "generating", "tool_executing"].includes(
        String(data.phase),
      )
    ) {
      phase = data.phase as typeof phase;
    } else if (event === "tool_call") {
      steps.push({
        ...(typeof data.call_id === "string" && data.call_id
          ? { callId: data.call_id }
          : {}),
        tool: String(data.tool),
        label: String(data.label),
        status: "running",
      });
      phase = "tool_executing";
    } else if (event === "tool_result") {
      const step =
        steps[
          findRunningToolStep(steps, { tool: data.tool, call_id: data.call_id })
        ];
      if (step) {
        step.status = "done";
        step.result = String(data.label ?? "") || undefined;
      }
      phase = "generating";
    } else if (event === "skill_invoked") {
      steps.push({
        ...(typeof data.call_id === "string" && data.call_id
          ? { callId: data.call_id }
          : {}),
        tool: `skill:${String(data.name ?? "")}`,
        label: String(data.label ?? `Loaded skill: ${String(data.name ?? "")}`),
        status: "done",
      });
    } else if (event === "data_block") {
      const block = data as unknown as DataBlockEventData;
      blocks.set(block.key, block);
    }
  }
  return { content, phase, steps, blocks };
}

export function useConversationProgress({
  conversationId,
  enabled,
  localStreaming,
  fetchStatus,
  onCompleted,
  onInput,
}: {
  conversationId: string;
  enabled: boolean;
  localStreaming: boolean;
  fetchStatus: (
    id: string,
    signal: AbortSignal,
  ) => Promise<ConversationProgress>;
  onCompleted: (id: string, state: ConversationProgress) => Promise<boolean>;
  onInput?: (id: string, state: ConversationProgress) => Promise<boolean>;
}) {
  const [state, setState] = useState<{
    id: string;
    snapshot: ConversationProgress;
  } | null>(null);
  const [checkedId, setCheckedId] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);
  const completed = useRef<string | null>(null);
  const loadedInput = useRef<string | null>(null);
  const reconnect = useCallback(() => setRefresh((n) => n + 1), []);
  /**
   * The tab that streamed this turn saw the server close the response, which
   * the route does only after the turn's lock is released. That close says
   * what the next poll would: this conversation can take a send. Clearing
   * `active` covers a poll that landed between `done` and the release. The
   * poll still runs to attach the saved message ID.
   */
  const settle = useCallback((id: string) => {
    setCheckedId(id);
    setState((prev) =>
      prev?.id === id && prev.snapshot.active
        ? { id, snapshot: { ...prev.snapshot, active: false } }
        : prev,
    );
  }, []);
  useEffect(() => {
    if (!enabled) return;
    if (localStreaming) {
      setCheckedId(null);
      return;
    }
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    let polling = false;
    const poll = async () => {
      if (polling || controller.signal.aborted) return;
      clearTimeout(timer);
      polling = true;
      let active = false;
      let failed = false;
      try {
        const snapshot = await fetchStatus(conversationId, controller.signal);
        if (controller.signal.aborted) return;
        active = snapshot.active;
        if (
          active &&
          snapshot.turn_id &&
          loadedInput.current !== snapshot.turn_id &&
          onInput
        ) {
          if (await onInput(conversationId, snapshot))
            loadedInput.current = snapshot.turn_id;
        }
        if (
          !active &&
          snapshot.turn_id &&
          completed.current !== snapshot.turn_id
        ) {
          // GSI history can lag the lock release. Keep watching until the
          // saved assistant ID is actually present, rather than showing idle.
          if (await onCompleted(conversationId, snapshot))
            completed.current = snapshot.turn_id;
          else active = true;
        }
        if (controller.signal.aborted) return;
        setState({ id: conversationId, snapshot: { ...snapshot, active } });
        setCheckedId(conversationId);
      } catch {
        // Preserve the last known running state during a transient outage.
        // The initial check remains disabled until the server can answer.
        failed = true;
      } finally {
        polling = false;
      }
      // Idle conversations need no watcher. Recheck on opening, returning to
      // the tab, or an explicit reconnect after a local stream completes.
      if (!controller.signal.aborted && (active || failed))
        timer = setTimeout(() => void poll(), active ? 2000 : 3000);
    };
    const onReturn = () => {
      if (document.visibilityState === "visible") void poll();
    };
    window.addEventListener("focus", onReturn);
    document.addEventListener("visibilitychange", onReturn);
    void poll();
    return () => {
      controller.abort();
      clearTimeout(timer);
      window.removeEventListener("focus", onReturn);
      document.removeEventListener("visibilitychange", onReturn);
    };
  }, [
    conversationId,
    enabled,
    localStreaming,
    fetchStatus,
    onCompleted,
    onInput,
    refresh,
  ]);
  return {
    snapshot: state?.id === conversationId ? state.snapshot : null,
    checking: enabled && !localStreaming && checkedId !== conversationId,
    reconnect,
    settle,
  };
}
